/** The sign-in screen's words and checks, from the design (L867–904, L1090–1118). */
import { z } from 'zod';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const MESSAGES = {
  badEmail: 'Enter a valid email address.',
  incomplete: 'Enter all six digits.',
  badCode: 'That code is not valid. Check the latest email.',
  throttled: 'Too many attempts. Try again later.',
  captcha: 'We could not confirm you are human. Try again.',
  unavailable: 'The security check is not responding. Try again in a moment.',
  failed: 'Something went wrong. Try again.',
} as const;

/** The email step's form: one address, trimmed, in the design's words when it is not one. */
export const emailFormSchema = z.object({
  email: z.string().trim().regex(EMAIL_SHAPE, MESSAGES.badEmail),
});

export type EmailForm = z.input<typeof emailFormSchema>;

export function looksLikeEmail(value: string): boolean {
  return emailFormSchema.safeParse({ email: value }).success;
}

/** What a failed `auth.code` or `auth.verify` tells the person. */
export function signInError(
  step: 'email' | 'code',
  status: number | undefined,
  body?: unknown,
): string {
  if (status === 429) return MESSAGES.throttled;
  if (status === 503) return MESSAGES.unavailable;
  if (status === 400 && errorOf(body) === 'captcha_failed') return MESSAGES.captcha;
  if (status === 400) return step === 'email' ? MESSAGES.badEmail : MESSAGES.badCode;
  return MESSAGES.failed;
}

/** The resend link: a countdown until the API allows another code. */
export function resendLabel(secondsLeft: number): string {
  return secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend code';
}

function errorOf(body: unknown): unknown {
  return typeof body === 'object' && body !== null
    ? (body as { error?: unknown }).error
    : undefined;
}
